import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowRight, Folder, FileText } from "lucide-react";
import { fetchCountAllProjects } from "@/store/thunks/dashboardThunk";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [totalStats, setTotalStats] = useState({
    totalProjects: 0,
    totalFiles: 0,
    totalWords: 0,
    totalSentences: 0
  });
  const { isAuthenticated } = useSelector((state) => state.auth);
  console.log("Auth State:", { isAuthenticated });

  useEffect(() => {
    try {
      if (!isAuthenticated ) return;
      const fetchInfo = async () => {
        const stats = await dispatch(fetchCountAllProjects());
        if (stats.error) {
          console.error("Failed to fetch total stats:", stats.error);
          return;
        }

        setTotalStats({
          totalProjects: stats.totalProjects || 0,
          totalFiles: stats.totalFiles || 0,
          totalWords: stats.totalWords || 0,
          totalSentences: stats.totalSentences || 0
        });
      }

      fetchInfo();
    } catch (e) {
      console.log(`use effect for superadmin dashboard failed`);
    }
  }, [dispatch,isAuthenticated]);

  const dashboardCards = [
    {
      id: "ga",
      title: "GA Solutions",
      description: "Access and manage GA content and translations.",
      route: "/superAdmin/ga",
      icon: <Folder className="w-8 h-8" />,
      gradient: "from-blue-50 to-indigo-50",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      id: "devnagri",
      title: "Devnagri",
      description: "Access and manage Devnagri content and translations.",
      route: "/superAdmin/devnagri",
      icon: <FileText className="w-8 h-8" />,
      gradient: "from-amber-50 to-orange-50",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600"
    }
  ];

  const handleCardClick = (route) => {
    navigate(route);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50 to-white pt-16">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-['Semantic/Strong'] font-bold text-gray-900 mb-3">
              SuperAdmin Dashboard
            </h1>
            <p className="text-lg text-gray-600 font-['Semantic/Strong']  mx-auto leading-relaxed">
              Centralized management hub for GA Solutions and Devnagri content. Access project details, manage files, and oversee translations with ease.
            </p>
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {dashboardCards.map((card) => (
            <div
              key={card.id}
              className={`group relative bg-gradient-to-br ${card.gradient} border border-gray-200 rounded-2xl p-8 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-[#ec7026]/20`}
              onClick={() => handleCardClick(card.route)}
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-6">
                <div className={`${card.iconBg} ${card.iconColor} p-4 rounded-xl transition-transform duration-300 group-hover:scale-110`}>
                  {card.icon}
                </div>
                <ArrowRight className="w-6 h-6 text-gray-400 transform transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#ec7026]" />
              </div>

              {/* Card Content */}
              <div className="space-y-4">
                <h2 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900 group-hover:text-[#ec7026] transition-colors duration-300">
                  {card.title}
                </h2>

                <p className="text-gray-600 font-['Semantic/Strong'] leading-relaxed">
                  {card.description}
                </p>



                {/* Action Button */}
                <button
                  className="w-full mt-6 bg-[#ec7026] hover:bg-[#d85d1a] text-white py-3 px-6 rounded-xl font-['Semantic/Strong'] font-semibold transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#ec7026]/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(card.route);
                  }}
                >
                  Access {card.title} Dashboard
                </button>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#ec7026]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* System Overview Section */}
        <div className="mt-16 max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden backdrop-blur-sm">
            <div className="bg-gradient-to-r from-[#ec7026]/5 via-[#ec7026]/2 to-transparent border-b border-gray-200 p-6">
              <h3 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900 flex items-center">
                <svg
                  className="w-6 h-6 mr-3 text-[#ec7026]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                System Overview
              </h3>
            </div>

            {/* Grid changed from 3 → 4 */}
            <div className="grid grid-cols-1 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">

              {/* Total Projects */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-50/0 group-hover:from-blue-50 group-hover:to-transparent transition-all duration-300"></div>
                <div className="relative p-8">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="p-3 rounded-xl bg-blue-100 text-blue-600 transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-['Semantic/Strong'] font-bold text-gray-900 mb-1 group-hover:text-blue-600">
                        {totalStats.totalProjects}
                      </div>
                      <div className="text-sm text-gray-600 font-['Semantic/Strong']">Total Projects</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Files */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50 group-hover:to-transparent transition-all duration-300"></div>
                <div className="relative p-8">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600 transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-['Semantic/Strong'] font-bold text-gray-900 mb-1 group-hover:text-emerald-600">
                        {totalStats.totalFiles}
                      </div>
                      <div className="text-sm text-gray-600 font-['Semantic/Strong']">Total Files</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Sentences */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/0 to-purple-50/0 group-hover:from-purple-50 group-hover:to-transparent transition-all duration-300"></div>
                <div className="relative p-8">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="p-3 rounded-xl bg-purple-100 text-purple-600 transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-['Semantic/Strong'] font-bold text-gray-900 mb-1 group-hover:text-purple-600">
                        {totalStats.totalSentences}
                      </div>
                      <div className="text-sm text-gray-600 font-['Semantic/Strong']">Total Sentences</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Words (NEW) */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-50/0 to-pink-50/0 group-hover:from-pink-50 group-hover:to-transparent transition-all duration-300"></div>
                <div className="relative p-8">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="p-3 rounded-xl bg-pink-100 text-pink-600 transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 20h9M12 4h9M4 8h16M4 16h16" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-['Semantic/Strong'] font-bold text-gray-900 mb-1 group-hover:text-pink-600">
                        {totalStats.totalWords}
                      </div>
                      <div className="text-sm text-gray-600 font-['Semantic/Strong']">Total Words</div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SuperAdminDashboard;